import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectCurrentCustomerNumber } from "../../../redux/customer";
import { Box, Button, Card, Link, useTheme } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Medication } from "./types";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import InfoIcon from "@mui/icons-material/Info";
import { getCorpChildrenMedication } from './services'

type Props = {
  count: number;
}

const BackorderedItems = ({ count }: Props) => {
  const theme = useTheme();
  const customerNumber = useSelector(selectCurrentCustomerNumber);
  const navigate = useNavigate();
  const [backorderedItems, setBackorderedItems] = useState<Medication[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [allLoaded, setAllLoaded] = useState<boolean>(false);

  const loadAdditionalData = async () => {
    setLoading(true);

    try {
      const queryParms = `?scope=backordered&limit=${count}&offset=100`
      const items = await getCorpChildrenMedication(customerNumber, queryParms)
      setBackorderedItems((currentItems) => ([...currentItems, ...items]));
      setLoading(false);
      setAllLoaded(true);
    } catch(error) {
      console.error(error);
      setLoading(false);
    }
  }

  useEffect(() => {
    if (customerNumber) {
      setLoading(true);

      (async () => {
        try {
          const queryParams = `?scope=backordered&limit=100`
          const items = await getCorpChildrenMedication(customerNumber, queryParams);
          setBackorderedItems(items);
          setLoading(false);
        } catch(error) {
          console.error(error);
          setLoading(false);
        }
      })()
    }
  }, [customerNumber])

  const getBackorderedNote = (status: number, complianceCode: string) => {
    if (status === 0) {
      return <span>To order item please contact Customer Service.</span>
    } else if (status === 1 && complianceCode !== 'EPREP_MED_EXPIRED_REFILL_SOON') {
      return <span>Item will be shipped when available.</span>
    } else if (status === 1 && complianceCode === 'EPREP_MED_EXPIRED_REFILL_SOON') {
      return <span>Item is scheduled to be refilled soon.</span>
    } else if (status === 2) {
      return <span>Purchase order will be sent when items are available.</span>
    } else if (status === 3) {
      return <span>Item will be shipped when available.</span>
    }
  }

  const getRefillStatus = (status: number) => {
    if (status === 1) {
      return <span className="color-success">Automatic Refill On</span>
    } else if (status === 2) {
      return <span className="color-warning">Refill On Approval</span>
    } else if (status === 4) {
      return <span className="color-warning">On Membership</span>
    } else if (status === 0) {
      return <span className="color-inactive">Automatic Refill Off</span>
    }
    return <span></span>
  }

  const getExpirationColor = (status: string) => {
    if (status === 'expired') {
      return 'color-danger';
    } else if (status === 'expiring') {
      return 'color-warning';
    }
    return 'color-inactive';
  }

  return (
    <>
      <Card variant="outlined">
        <div className="box-header">{backorderedItems.length} Backordered Items</div>
        <DataGrid
          sx={{ borderLeft: "none", borderRight: "none", borderBottom: "none", borderRadius: "0" }}
          rows={backorderedItems.map(item => ({ ...item, id: `${item.kit_id}${Math.floor(Math.random() * (9999 - 1000 + 1) + 1000)}` }))}
          columns={[
            {
              field: "name",
              headerName: "Name",
              flex: 1,
              minWidth: 400,
              renderCell: (params) => (
                <Link component="button" onClick={() => navigate(`/corp/practice/${params.row.customer_number}/medications`)} sx={{ color: theme.palette.linkColor.main, textDecoration: 'none' }}>
                  {params.row.name.trim()} ({params.row.customer_number})
                </Link>
              )
            },
            {
              field: "city",
              headerName: "Location",
              flex: 1,
              minWidth: 250,
              renderCell: (params) => (
                `${params.row.city}, ${params.row.state}`
              )
            },
            {
              field: "kit_type",
              headerName: "Kit",
              flex: 1,
              minWidth: 100
            },
            {
              field: "item_refill_status",
              headerName: "Refill",
              flex: 1,
              minWidth: 200,
              renderCell: (params) => (
                getRefillStatus(params.row.item_refill_status)
              )
            },
            {
              field: "item_description",
              headerName: "Item",
              flex: 1,
              minWidth: 400,
              renderCell: (params) => (
                <span>{params.row.quantity} {params.row.item_description}</span>
              )
            },
            {
              field: "expiration_date",
              headerName: "Expiration Date",
              flex: 1,
              minWidth: 200,
              renderCell: (params) => (
                <span className={getExpirationColor(params.row.status)}>{params.row.expiration_date ? dayjs(params.row.expiration_date).format("MM/DD/YYYY") : ''}</span>
              )
            },
            {
              field: "item_compliance_code",
              headerName: "Note",
              flex: 1,
              minWidth: 300,
              renderCell: (params) => (
                getBackorderedNote(params.row.item_refill_status, params.row.item_compliance_code)
              )
            }
          ]}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
            sorting: {
              sortModel: [
                {
                  field: 'name',
                  sort: 'asc'
                }
              ]
            }
          }}
          pageSizeOptions={[5, 10, 20, 25, 50, 100]}
          loading={loading}
          autoHeight
          density="compact"
        />
        {count > 100 && !allLoaded && (
          <div className="flex flex-center m-b-16"><Button onClick={loadAdditionalData}>Load additional data</Button></div>)
        }
      </Card>
      <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '16px', fontSize: '13px' }}>
        <InfoIcon fontSize="small" className="m-l-8 m-r-8 gray-icon" /> Items are on backorder due to manufacturer shortages. For
        more information on national drug shortages, visit: <Link target="_blank" rel="noopener" href="http://www.ashp.org/shortages">ashp.org</Link> or <Link target="_blank" rel="noopener" href="https://dps.fda.gov/drugshortages">accessdata.fda.gov</Link>
      </Box>
    </>
  )
}

export default BackorderedItems;
